import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { themes, loadTheme, applyTheme } from '../themes';
import { supportedLanguages } from '../i18n';
import { useState } from 'react';
import { useSession } from '../lib/session';
import { safeConversionPath } from '../lib/sessionFlow';
import TrialNotice from './TrialNotice';

export default function Layout() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const session = useSession(state => state.state);
  const navigate = useNavigate();
  const [currentTheme, setCurrentTheme] = useState(loadTheme().id);

  const handleThemeChange = (themeId: string) => {
    const theme = themes.find(t => t.id === themeId);
    if (theme) {
      applyTheme(theme);
      setCurrentTheme(themeId);
    }
  };

  const handleLogoutClick = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="app-shell">
      <header
        style={{
          background: 'var(--bg-secondary)',
          borderBottom: '1px solid var(--border)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backdropFilter: 'blur(12px)',
        }}
      >
        <div className="app-header-inner">
          {/* Logo */}
          <NavLink to="/scan" className="app-brand">
            <div
              className="app-brand-mark"
              style={{
                background: 'var(--accent-primary)',
                borderRadius: 'var(--radius-sm)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                fontWeight: 900,
                color: 'var(--bg-primary)',
                fontFamily: 'var(--font-display)',
              }}
            >
              CC
            </div>
            <span
              className="app-brand-text"
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: 18,
                color: 'var(--text-primary)',
                letterSpacing: '-0.02em',
              }}
            >
              ComplianceCheck
            </span>
          </NavLink>

          {/* Nav */}
          <nav className="app-nav">
            {[
              { to: '/scan', label: t('nav.newScan') },
              { to: '/history', label: t('nav.history') },
              { to: '/partner', label: t('nav.partner') },
              { to: '/templates', label: t('nav.templates') },
            ].map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                style={({ isActive }) => ({
                  padding: '6px 14px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 14,
                  fontWeight: 500,
                  color: isActive
                    ? 'var(--accent-primary)'
                    : 'var(--text-secondary)',
                  background: isActive ? 'var(--accent-dim)' : 'transparent',
                  transition: 'all 0.15s',
                  textDecoration: 'none',
                })}
              >
                {label}
              </NavLink>
            ))}
          </nav>

          {/* Controls */}
          <div className="app-controls">
            {/* Language switcher */}
            <select
              aria-label={t('nav.language')}
              value={i18n.language.split('-')[0]}
              onChange={e => i18n.changeLanguage(e.target.value)}
              style={{
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border)',
                color: 'var(--text-secondary)',
                borderRadius: 'var(--radius-sm)',
                padding: '4px 8px',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {supportedLanguages.map(l => (
                <option key={l.code} value={l.code}>
                  {l.flag} {l.code.toUpperCase()}
                </option>
              ))}
            </select>

            {/* Theme switcher */}
            <select
              aria-label={t('nav.theme')}
              value={currentTheme}
              onChange={e => handleThemeChange(e.target.value)}
              style={{
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border)',
                color: 'var(--text-secondary)',
                borderRadius: 'var(--radius-sm)',
                padding: '4px 8px',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {themes.map(t => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>

            {/* User */}
            {session?.principalKind === 'anonymous' && (
              <NavLink
                to={safeConversionPath(session.conversionPath)}
                className="button button-secondary"
              >
                {t('trial.createAccount')}
              </NavLink>
            )}
            {user && session?.principalKind === 'authenticated' && (
              <div className="app-user">
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.name}
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      border: '2px solid var(--border)',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      background: 'var(--accent-dim)',
                      border: '2px solid var(--accent-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      color: 'var(--accent-primary)',
                    }}
                  >
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <span
                  className="app-user-name"
                  style={{ fontSize: 13, color: 'var(--text-secondary)' }}
                >
                  {user.name?.split(' ')[0]}
                </span>
                <button
                  onClick={handleLogoutClick}
                  style={{
                    background: 'none',
                    border: '1px solid var(--border)',
                    color: 'var(--text-muted)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '4px 10px',
                    fontSize: 12,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                  onMouseOver={e => {
                    (e.currentTarget as HTMLButtonElement).style.color =
                      'var(--danger)';
                    (e.currentTarget as HTMLButtonElement).style.borderColor =
                      'var(--danger)';
                  }}
                  onMouseOut={e => {
                    (e.currentTarget as HTMLButtonElement).style.color =
                      'var(--text-muted)';
                    (e.currentTarget as HTMLButtonElement).style.borderColor =
                      'var(--border)';
                  }}
                >
                  {t('nav.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <TrialNotice />
      <main className="app-main">
        <div className="app-main-inner">
          <Outlet />
        </div>
      </main>

      <footer className="app-footer">{t('app.disclaimer')}</footer>
    </div>
  );
}
